<script setup>
import { useLayout } from '@/layout/composables/layout';
import { computed, onMounted, onUnmounted } from 'vue';
import AppBreadcrumb from './AppBreadcrumb.vue';
import TopbarCasePicker from '@/components/crm/TopbarCasePicker.vue';
import { auth } from '@/firebaseInit';
import { signOut } from 'firebase/auth';
import { useRouter } from 'vue-router';
import { useNotifications } from '@/composables/useNotifications';

const router = useRouter();
const { isDarkTheme, toggleMenu } = useLayout();
const { unreadCount, loadUnreadCount } = useNotifications();

let pollInterval = null;

// User data and avatar
const user = computed(() => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
});

const userAvatar = computed(() => {
    if (user.value) {
        const name = user.value.full_name || user.value.name || user.value.username || user.value.email || 'User';
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&bold=true&size=128`;
    }
    return null;
});

async function handleLogout() {
    try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        await signOut(auth);
        router.push('/landing');
    } catch (error) {
        console.error('Error signing out:', error);
    }
}

onMounted(() => {
    loadUnreadCount();
    pollInterval = setInterval(loadUnreadCount, 120000);
});

onUnmounted(() => {
    if (pollInterval) clearInterval(pollInterval);
});
</script>

<template>
    <div class="layout-topbar">
        <div class="topbar-left">
            <a tabindex="0" class="menu-button" @click="toggleMenu">
                <i class="pi pi-chevron-left" />
            </a>
            <img class="horizontal-logo" src="/layout/images/lfi.svg" alt="Law Firm Intelligence" />
            <span class="topbar-separator" />
            <AppBreadcrumb />
            <RouterLink to="/"><img class="mobile-logo" :src="`/layout/images/logo-${isDarkTheme ? 'white' : 'dark'}.svg`" alt="poseidon-layout" /></RouterLink>
        </div>

        <div class="topbar-right">
            <ul class="topbar-menu">
                <TopbarCasePicker />
                <!-- <li class="right-sidebar-item">
                    <button type="button" class="app-config-button" @click="toggleConfigSidebar" aria-label="Toggle Config Sidebar">
                        <i class="pi pi-cog" />
                    </button>
                </li> -->
                <li class="right-sidebar-item">
                    <RouterLink to="/" class="right-sidebar-button relative">
                        <span v-if="unreadCount > 0" class="w-2 h-2 rounded-full bg-red-500 absolute top-2 right-2.5" />
                        <i class="pi pi-bell" />
                    </RouterLink>
                </li>

                <li class="profile-item static sm:relative">
                    <a
                        class="bg-none! border-none! outline-none! flex items-center cursor-pointer"
                        v-styleclass="{ selector: '@next', enterFromClass: 'hidden', enterActiveClass: 'p-anchored-overlay-enter-active', leaveActiveClass: 'p-anchored-overlay-leave-active', leaveToClass: 'hidden', hideOnOutsideClick: true }"
                    >
                        <img v-if="userAvatar" :src="userAvatar" alt="User Avatar" class="w-10 h-10 rounded-full border-2 border-surface-200 dark:border-surface-700 hover:border-primary-500 transition-colors" />
                        <i v-else class="pi pi-user" style="font-size: 2.5rem" />
                    </a>
                    <div
                        class="list-none p-2 m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-52 mt-2 right-0 z-[999] top-auto shadow-[0px_56px_16px_0px_rgba(0,0,0,0.00),0px_36px_14px_0px_rgba(0,0,0,0.01),0px_20px_12px_0px_rgba(0,0,0,0.02),0px_9px_9px_0px_rgba(0,0,0,0.03),0px_2px_5px_0px_rgba(0,0,0,0.04)]"
                    >
                        <!-- User info header -->
                        <div v-if="user" class="px-2.5 py-3 border-b border-surface-200 dark:border-surface-700 mb-2">
                            <div class="flex items-center gap-3">
                                <img v-if="userAvatar" :src="userAvatar" alt="User Avatar" class="w-10 h-10 rounded-full" />
                                <div class="flex-1 overflow-hidden">
                                    <p class="font-medium text-surface-950 dark:text-surface-0 truncate">
                                        {{ user.full_name || user.name || user.username || 'User' }}
                                    </p>
                                    <p class="text-sm text-surface-600 dark:text-surface-400 truncate">
                                        {{ user.email }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <ul class="flex flex-col gap-1">
                            <!-- <li>
                                <a class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                    <i class="pi pi-user" />
                                    <span>Profile</span>
                                </a>
                            </li>
                            <li>
                                <a class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                    <i class="pi pi-cog" />
                                    <span>Settings</span>
                                </a>
                            </li>
                            <li>
                                <a class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                    <i class="pi pi-calendar" />
                                    <span>Calendar</span>
                                </a>
                            </li>
                            <li>
                                <a class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                    <i class="pi pi-inbox" />
                                    <span>Inbox</span>
                                </a>
                            </li> -->
                            <li>
                                <a @click="handleLogout" class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis cursor-pointer">
                                    <i class="pi pi-power-off" />
                                    <span>Log out</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </li>

                <!-- <li class="right-sidebar-item">
                    <a tabindex="0" class="right-sidebar-button" @click="showRightMenu">
                        <i class="pi pi-align-right" />
                    </a>
                </li> -->
            </ul>
        </div>
    </div>
</template>
